import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Cookie, Shield, Settings, ChevronRight } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { CONSENT_VERSION, readConsent, writeConsent } from '@/lib/consent';

export function CookieConsent() {
    const { language } = useLanguage();
    const isGerman = language === 'de';
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [consent, setConsent] = useState({
        necessary: true, // Always true, cannot be disabled
        functional: false,
        analytics: false,
        marketing: false,
    });

    useEffect(() => {
        const storedConsent = readConsent();
        if (storedConsent) {
            setConsent(storedConsent.consent);
            return;
        }
        // Show banner after a short delay
        const timer = setTimeout(() => setShowBanner(true), 1000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const openSettings = () => {
            const storedConsent = readConsent();
            if (storedConsent) setConsent(storedConsent.consent);
            setShowSettings(true);
        };
        window.addEventListener('openCookieSettings', openSettings);
        return () => window.removeEventListener('openCookieSettings', openSettings);
    }, []);

    const saveConsent = async (newConsent) => {
        const timestamp = new Date().toISOString();
        let savedConsent = writeConsent(newConsent, { timestamp });
        
        setConsent(newConsent);
        setShowBanner(false);
        setShowSettings(false);

        // Send consent to backend for GDPR compliance
        try {
            const response = await publicApi.logConsent({
                categories: newConsent,
                policy_version: CONSENT_VERSION,
            });
            savedConsent = writeConsent(newConsent, {
                consentId: response.data.consent_id,
                timestamp,
            });
        } catch (error) {
            console.error('Failed to save consent to backend:', error);
        }
        
        // Dispatch event for analytics to pick up
        window.dispatchEvent(new CustomEvent('consentUpdated', {
            detail: { ...newConsent, consent_id: savedConsent.consent_id },
        }));
    };

    const acceptAll = () => {
        saveConsent({
            necessary: true,
            functional: true,
            analytics: true,
            marketing: true,
        });
    };

    const acceptNecessary = () => {
        saveConsent({
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false,
        });
    };

    const saveSettings = () => {
        saveConsent(consent);
    };

    const cookieCategories = isGerman ? [
        {
            key: 'necessary',
            title: 'Notwendige Cookies',
            description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
            required: true,
        },
        {
            key: 'functional',
            title: 'Funktionale Cookies',
            description: 'Diese Cookies ermöglichen erweiterte Funktionen wie Spracheinstellungen und personalisierte Inhalte.',
            required: false,
        },
        {
            key: 'analytics',
            title: 'Analyse Cookies',
            description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.',
            required: false,
        },
        {
            key: 'marketing',
            title: 'Marketing Cookies',
            description: 'Diese Cookies werden verwendet, um Werbung relevanter für Sie zu gestalten.',
            required: false,
        },
    ] : [
        {
            key: 'necessary',
            title: 'Necessary Cookies',
            description: 'These cookies are required for the basic functions of the website and cannot be disabled.',
            required: true,
        },
        {
            key: 'functional',
            title: 'Functional Cookies',
            description: 'These cookies enable enhanced features such as language settings and personalized content.',
            required: false,
        },
        {
            key: 'analytics',
            title: 'Analytics Cookies',
            description: 'These cookies help us understand how visitors interact with the website.',
            required: false,
        },
        {
            key: 'marketing',
            title: 'Marketing Cookies',
            description: 'These cookies are used to make advertising more relevant to you.',
            required: false,
        },
    ];

    if (!showBanner && !showSettings) return null;

    return (
        <>
            {/* Cookie Banner */}
            {showBanner && !showSettings && (
                <div 
                    className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-lg border-t shadow-lg"
                    data-testid="cookie-banner"
                    role="region"
                    aria-labelledby="cookie-banner-title"
                >
                    <div className="container mx-auto max-w-6xl">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                            <div className="flex items-start gap-4 flex-grow">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <Cookie className="h-6 w-6 text-primary" />
                                </div>
                                <div>
                                    <h3 id="cookie-banner-title" className="font-semibold mb-1">
                                        {isGerman ? 'Cookie-Einstellungen' : 'Cookie Settings'}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        {isGerman 
                                            ? 'Optionale Dienste werden erst nach Ihrer Einwilligung geladen. Ablehnen ist genauso einfach wie Akzeptieren; Ihre Auswahl können Sie jederzeit ändern.'
                                            : 'We use cookies to provide you with the best possible experience on our website. You can adjust your settings at any time.'}
                                    </p>
                                    <a 
                                        href="/datenschutz" 
                                        className="text-sm text-primary hover:underline inline-flex items-center mt-1"
                                    >
                                        {isGerman ? 'Mehr erfahren' : 'Learn more'}
                                        <ChevronRight className="h-3 w-3" aria-hidden="true" />
                                    </a>
                                </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2 w-full md:w-auto">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowSettings(true)}
                                    className="flex-1 md:flex-none"
                                    data-testid="cookie-settings-btn"
                                >
                                    <Settings className="h-4 w-4 mr-2" />
                                    {isGerman ? 'Einstellungen' : 'Settings'}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={acceptNecessary}
                                    className="flex-1 md:flex-none"
                                    data-testid="cookie-reject-btn"
                                >
                                    {isGerman ? 'Nur notwendige' : 'Necessary only'}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={acceptAll}
                                    className="flex-1 md:flex-none"
                                    data-testid="cookie-accept-btn"
                                >
                                    <Shield className="h-4 w-4 mr-2" />
                                    {isGerman ? 'Alle akzeptieren' : 'Accept all'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cookie Settings Dialog */}
            <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Cookie className="h-5 w-5" />
                            {isGerman ? 'Cookie-Einstellungen' : 'Cookie Settings'}
                        </DialogTitle>
                        <DialogDescription>
                            {isGerman 
                                ? 'Hier können Sie Ihre Cookie-Präferenzen verwalten. Notwendige Cookies sind für die Funktion der Website erforderlich und können nicht deaktiviert werden.'
                                : 'Here you can manage your cookie preferences. Necessary cookies are required for the website to function and cannot be disabled.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {cookieCategories.map((category) => (
                            <Card key={category.key} data-testid={`cookie-category-${category.key}`}>
                                <CardHeader className="pb-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <CardTitle className="text-base">
                                                <Label id={`cookie-label-${category.key}`} htmlFor={`cookie-switch-${category.key}`}>
                                                    {category.title}
                                                </Label>
                                            </CardTitle>
                                            {category.required && (
                                                <Badge variant="secondary" className="text-xs">
                                                    {isGerman ? 'Erforderlich' : 'Required'}
                                                </Badge>
                                            )}
                                        </div>
                                        <Switch
                                            id={`cookie-switch-${category.key}`}
                                            checked={consent[category.key]}
                                            onCheckedChange={(checked) => 
                                                setConsent({ ...consent, [category.key]: checked })
                                            }
                                            disabled={category.required}
                                            aria-labelledby={`cookie-label-${category.key}`}
                                            aria-describedby={`cookie-description-${category.key}`}
                                            data-testid={`cookie-switch-${category.key}`}
                                        />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p id={`cookie-description-${category.key}`} className="text-sm text-muted-foreground">
                                        {category.description}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={acceptNecessary}>
                            {isGerman ? 'Nur notwendige' : 'Necessary only'}
                        </Button>
                        <Button onClick={saveSettings} data-testid="cookie-save-settings-btn">
                            {isGerman ? 'Einstellungen speichern' : 'Save settings'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default CookieConsent;
